import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Button,
    Stack,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    Grid,
    Switch,
    FormControlLabel
} from '@mui/material';
import { Plus, SquarePen, Trash2, ArrowLeft, ClipboardList } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { getJobRoles, createJobRole, updateJobRole, getJobCategories, getJobRolesByCategory, deleteJobRole } from '../../../Services/ContentHub/JobsApi/JobsApi';
import DeleteModal from '../../../Components/Modals/DeleteModal';
import { useGlobalSnackbar } from '../../../Components/Common/GlobalSanckbar';

const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
    });
};

const JobRoles = () => {
    const { showSnackbar } = useGlobalSnackbar();
    const location = useLocation();
    const { categoryId, categoryName } = location.state || {};
    const [roles, setRoles] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [fieldErrors, setFieldErrors] = useState({});
    const navigate = useNavigate();

    // Modal state
    const [openModal, setOpenModal] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [editId, setEditId] = useState(null);
    const [form, setForm] = useState({
        categoryId: categoryId || '',
        title: '',
        description: '',
        isActive: true
    });

    // Delete modal states
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectedRoleId, setSelectedRoleId] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState(null);

    const fetchRoles = useCallback(async (signal) => {
        try {
            setLoading(true);
            let response;
            if (categoryId) {
                response = await getJobRolesByCategory(categoryId);
            } else {
                response = await getJobRoles(signal);
            }

            if (response && response.success) {
                setRoles(response.data);
            } else {
                showSnackbar("Failed to load job roles.", "error");
            }
        } catch (err) {
            if (err.name !== 'CanceledError' && !err.message?.includes('canceled')) {
                showSnackbar("Failed to fetch job roles.", "error");
            }
        } finally {
            setLoading(false);
        }
    }, [categoryId, showSnackbar]);

    const fetchCategories = useCallback(async () => {
        try {
            const response = await getJobCategories();
            if (response && response.success) {
                setCategories(response.data);
            }
        } catch (err) {
            console.error("Failed to fetch categories for dropdown");
        }
    }, []);

    useEffect(() => {
        const controller = new AbortController();
        fetchRoles(controller.signal);
        fetchCategories();
        return () => controller.abort();
    }, [fetchRoles, fetchCategories]);

    const handleOpenModal = (role = null) => {
        setFieldErrors({});
        if (role) {
            setForm({
                categoryId: role.categoryId || role.category_id || categoryId || '',
                title: role.title,
                description: role.description || '',
                isActive: role.isActive
            });
            setEditId(role.id);
        } else {
            setForm({ categoryId: categoryId || '', title: '', description: '', isActive: true });
            setEditId(null);
        }
        fetchCategories();
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setFieldErrors({});
    };

    const handleSubmit = async () => {
        const errors = {};
        if (!form.categoryId) {
            errors.categoryId = "Category is required";
        }
        if (!form.title.trim()) {
            errors.title = "Job Title is required";
        }

        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            return;
        }
        setFieldErrors({});

        setSubmitting(true);
        try {
            let response;
            if (editId) {
                response = await updateJobRole(editId, form);
            } else {
                response = await createJobRole(form);
            }

            if (response && response.success) {
                showSnackbar(editId ? "Job Role updated successfully!" : "Job Role created successfully!", "success");
                handleCloseModal();
                fetchRoles();
            } else {
                showSnackbar(response.message || "Failed to save job role", "error");
            }
        } catch (err) {
            console.error(err);
            const serverError = err.response?.data;
            const errorMsg = serverError?.errors?.[0] ||
                serverError?.details?.[0]?.message ||
                serverError?.message ||
                err.message ||
                "An error occurred while saving job role";

            showSnackbar(errorMsg, "error");

            const errorsObj = {};
            if (serverError && serverError.details && Array.isArray(serverError.details)) {
                serverError.details.forEach(item => {
                    errorsObj[item.field] = item.message;
                });
                setFieldErrors(errorsObj);
            } else if (serverError && serverError.errors && Array.isArray(serverError.errors) && serverError.errors.length > 0) {
                if (serverError.errors[0].toLowerCase().includes("description")) {
                    errorsObj['description'] = serverError.errors[0];
                } else {
                    errorsObj['title'] = serverError.errors[0];
                }
                setFieldErrors(errorsObj);
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteClick = (id) => {
        setSelectedRoleId(id);
        setDeleteError(null);
        setDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (selectedRoleId) {
            try {
                setIsDeleting(true);
                setDeleteError(null);
                const response = await deleteJobRole(selectedRoleId);
                if (response && response.success) {
                    setDeleteModalOpen(false);
                    setSelectedRoleId(null);
                    showSnackbar("Job role deleted successfully!", "success");
                    fetchRoles();
                } else {
                    setDeleteError(response?.message || "Failed to delete job role");
                }
            } catch (err) {
                console.error("Delete Error:", err);
                const errorMessage = err.response?.data?.message || err.message || "Failed to delete job role";
                setDeleteError(errorMessage);
            } finally {
                setIsDeleting(false);
            }
        }
    };

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#FBFBFB', minHeight: '100vh' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, gap: 2 }}>
                <IconButton disableFocusRipple disableRipple disableTouchRipple onClick={() => navigate('/content-hub/jobs/job-categories')} sx={{ bgcolor: '#fff', border: '1px solid #E8E8E8', borderRadius: '6px', p: 1 }}>
                    <ArrowLeft size={16} />
                </IconButton>
                <Box sx={{ flexGrow: 1 }}>
                    <Typography sx={{ color: '#222', fontSize: { xs: '1.75rem', md: '1.8rem' }, fontWeight: 500 }}>
                        {categoryName ? `Job Roles - ${categoryName}` : 'Job Roles'}
                    </Typography>
                </Box>
                <Button
                    disableElevation disableRipple disableFocusRipple disableTouchRipple
                    variant="contained"
                    startIcon={<Plus size={18} />}
                    onClick={() => handleOpenModal()}
                    sx={{
                        bgcolor: '#FFBD65',

                        px: 2.5,
                        color: '#fff',
                        borderRadius: '6px',
                        height: '40px',
                        textTransform: 'none',
                        fontSize: '0.85rem'
                    }}
                >
                    Add Job Role
                </Button>
            </Box>

            <Paper sx={{ borderRadius: "8px", border: "1px solid #E8E8E8", overflow: "hidden" }} elevation={0}>
                <TableContainer>
                    <Table sx={{ minWidth: 950 }}>
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#F8FAFC', borderBottom: '2px solid #EEF2F6' }}>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', textAlign: 'center', width: '60px' }}>S.No</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap' }}>Job Title</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '100px' }}>Status</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '130px' }}>Created By</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '130px' }}>Updated By</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '120px' }}>Created At</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '120px' }}>Updated At</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '150px' }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                                        <CircularProgress size={24} sx={{ color: '#FFBD65' }} />
                                    </TableCell>
                                </TableRow>
                            ) : roles.length > 0 ? (
                                roles.map((role, idx) => (
                                    <TableRow key={role.id} sx={{
                                        transition: 'background-color 0.2s',
                                        '& td': { borderBottom: '1px solid #F0F0F0' },
                                        '&:last-child td': { borderBottom: 0 }
                                    }}>
                                        <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#666', textAlign: 'center' }}>{idx + 1}</TableCell>
                                        <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', fontWeight: 550, color: '#333' }}>
                                            <Box>
                                                <Typography sx={{ fontWeight: 600, color: '#333', fontSize: '0.85rem' }}>{role.title}</Typography>
                                                {!categoryName && (
                                                    <Typography variant="caption" sx={{ fontSize: '0.75rem', color: '#999' }}>
                                                        {role.category_name || '-'}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ py: 1.25, px: 1.5 }}>
                                            <Box sx={{
                                                display: 'inline-block',
                                                px: 1.5, py: 0.3,
                                                borderRadius: '4px',
                                                bgcolor: role.isActive ? '#E8F5E9' : '#FFEBEE',
                                                color: role.isActive ? '#2E7D32' : '#D32F2F',
                                                fontSize: '0.7rem', fontWeight: 600
                                            }}>
                                                {role.isActive ? 'Active' : 'Inactive'}
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                            {role.cuName || '-'}
                                        </TableCell>
                                        <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                            {role.uuName || '-'}
                                        </TableCell>
                                        <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                            {formatDate(role.createdAt)}
                                        </TableCell>
                                        <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                            {formatDate(role.updatedAt)}
                                        </TableCell>
                                        <TableCell align="right" sx={{ py: 1.25, px: 1.5 }}>
                                            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                                <Tooltip title="Edit">
                                                    <IconButton disableFocusRipple disableRipple disableTouchRipple onClick={() => handleOpenModal(role)} size="small" sx={{ color: '#FFBD65' }}>
                                                        <SquarePen size={16} />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete">
                                                    <IconButton
                                                        disableFocusRipple
                                                        disableRipple
                                                        disableTouchRipple
                                                        size="small"
                                                        sx={{ color: '#FF4D49' }}
                                                        onClick={() => handleDeleteClick(role.id)}
                                                    >
                                                        <Trash2 size={16} />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="View Applications">
                                                    <IconButton
                                                        disableFocusRipple disableRipple disableTouchRipple
                                                        onClick={() => navigate('/content-hub/jobs/job-enquiries', { state: { jobId: role.id, jobTitle: role.title } })}
                                                        size="small" sx={{ color: '#FFBD65' }}
                                                    >
                                                        <ClipboardList size={16} />
                                                    </IconButton>
                                                </Tooltip>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 4, color: '#AAA', fontSize: '0.8rem' }}>
                                        No job roles found.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* Modal */}
            <Dialog open={openModal} onClose={handleCloseModal} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '6px' } }}>
                <DialogTitle sx={{ fontWeight: 600, color: '#222', fontSize: '1.1rem', pb: 1 }}>{editId ? 'Edit Job Role' : 'Add Job Role'}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 2 }}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                select fullWidth label="Category" size="small"
                                value={form.categoryId}
                                onChange={(e) => {
                                    setForm({ ...form, categoryId: e.target.value });
                                    if (fieldErrors.categoryId) {
                                        setFieldErrors(prev => ({ ...prev, categoryId: '' }));
                                    }
                                }}
                                disabled={Boolean(categoryId)}
                                error={Boolean(fieldErrors.categoryId)}
                                helperText={fieldErrors.categoryId}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '6px',
                                        fontSize: '0.85rem',
                                        '& fieldset': { borderColor: '#E0E0E0' },
                                        '&:hover fieldset': { borderColor: '#BDBDBD' },
                                        '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                    }
                                }}
                            >
                                {categories.map((cat) => (
                                    <MenuItem key={cat.id} value={cat.id} sx={{ fontSize: '0.85rem' }}>{cat.name}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth size="small"
                                label="Job Title"
                                placeholder="e.g. Senior Wellness Specialist"
                                value={form.title}
                                inputProps={{ maxLength: 100 }}
                                onChange={(e) => {
                                    const sanitized = e.target.value.replace(/[^a-zA-Z\s]/g, '').slice(0, 100);
                                    setForm({ ...form, title: sanitized });
                                    if (fieldErrors.title) {
                                        setFieldErrors(prev => ({ ...prev, title: '' }));
                                    }
                                }}
                                error={Boolean(fieldErrors.title)}
                                helperText={fieldErrors.title}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '6px',
                                        fontSize: '0.85rem',
                                        '& fieldset': { borderColor: '#E0E0E0' },
                                        '&:hover fieldset': { borderColor: '#BDBDBD' },
                                        '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                    }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="body2" sx={{ mb: 1, color: fieldErrors.description ? '#d32f2f' : '#333', fontWeight: 550, fontSize: '0.85rem' }}>Description</Typography>
                            <Box sx={{
                                '& .quill': {
                                    bgcolor: '#fff',
                                    borderRadius: '6px',
                                    '& .ql-toolbar': {
                                        borderTopLeftRadius: '6px',
                                        borderTopRightRadius: '6px',
                                        borderColor: fieldErrors.description ? '#d32f2f' : '#E0E0E0'
                                    },
                                    '& .ql-container': {
                                        borderBottomLeftRadius: '6px',
                                        borderBottomRightRadius: '6px',
                                        borderColor: fieldErrors.description ? '#d32f2f' : '#E0E0E0',
                                        minHeight: '200px',
                                        fontSize: '0.85rem'
                                    }
                                }
                            }}>
                                <ReactQuill
                                    theme="snow"
                                    value={form.description}
                                    onChange={(value) => {
                                        setForm({ ...form, description: value });
                                        if (fieldErrors.description) {
                                            setFieldErrors(prev => ({ ...prev, description: '' }));
                                        }
                                    }}
                                    modules={{
                                        toolbar: [
                                            [{ 'header': [1, 2, false] }],
                                            ['bold', 'italic', 'underline', 'strike'],
                                            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                                            ['link', 'clean']
                                        ],
                                    }}
                                />
                            </Box>
                            {fieldErrors.description && (
                                <Typography variant="caption" sx={{ color: '#d32f2f', mt: 0.5, display: 'block', fontSize: '0.75rem' }}>
                                    {fieldErrors.description}
                                </Typography>
                            )}
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel control={<Switch checked={form.isActive}
                                onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                                sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#FFBD65' },
                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#FFBD65' }
                                }}
                            />
                            }
                                label={<Typography sx={{ fontSize: '0.85rem' }}>Active State</Typography>}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2.5, pt: 1 }}>
                    <Button disableElevation disableFocusRipple disableRipple disableTouchRipple onClick={handleCloseModal} sx={{ color: '#222', textTransform: 'none', fontSize: '0.85rem', px: 2.5 }}>Cancel</Button>
                    <Button disableElevation disableFocusRipple disableRipple disableTouchRipple
                        onClick={handleSubmit}
                        disabled={submitting}
                        variant="contained"
                        sx={{
                            bgcolor: '#FFBD65',
                            color: '#fff',
                            textTransform: 'none',
                            borderRadius: '6px',
                            px: 3,
                            fontSize: '0.85rem'
                        }}
                    >
                        {submitting ? <CircularProgress size={20} color="inherit" /> : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            <DeleteModal
                open={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                onDelete={handleConfirmDelete}
                title="Delete Job Role"
                description="Are you sure you want to delete this job role? This action cannot be undone."
                error={deleteError}
                loading={isDeleting}
            />
        </Box>
    );
};

export default JobRoles;

